import React, { FunctionComponent, useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import dayjs from "utils/dayjs"
import * as Comlink from "comlink"

import { Flex } from "components/Flex"
import Text from "components/Text/Text"
import Button from "components/Button/Button"

import Modal, { IModalProps } from "components/Modal/Modal"
import workers from "@/workers"
import { Guid } from "@/types"

const SessionTimeout = workers.SessionTimeout()

interface IContinueSessionModalProps extends IModalProps {
    sessionId: Guid
    duration: ReturnType<typeof dayjs.duration>
    onContinue(): Promise<void> | void
    onExpire(): Promise<void> | void
}

const ContinueSessionModal: FunctionComponent<React.PropsWithChildren<IContinueSessionModalProps>> = ({
    sessionId,
    duration,
    onContinue,
    onExpire,
}) => {
    const { t } = useTranslation()
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [secondsLeft, setSecondsLeft] = useState(() => duration.asSeconds())
    const pageTitleModifier = "❗ "

    const onTick = (newSecondsLeft: number) => {
        setSecondsLeft(newSecondsLeft)
        if (document.title.startsWith(pageTitleModifier)) {
            resetPageTitle()
        } else {
            modifyPageTitle()
        }
    }

    useEffect(() => {
        SessionTimeout.start(
            sessionId,
            duration.asSeconds(),
            Comlink.proxy(onTick),
            Comlink.proxy(onExpire),
        )

        return () => {
            resetPageTitle();
            SessionTimeout.clear(sessionId)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId])

    function modifyPageTitle() {
        document.title = `${pageTitleModifier}${document.title}`
    }

    function resetPageTitle() {
        if (document.title.startsWith(pageTitleModifier)) {
            document.title = document.title.substring(pageTitleModifier.length)
        }
    }

    async function handleContinue() {
        setIsRefreshing(true)
        await onContinue()
        setIsRefreshing(false);
        SessionTimeout.clear(sessionId)
    }

    return (
        <Modal
            title={t('common.warning')}
            maxWidth="640px"
            footer={
                <Flex
                    justifyContent="flex-end"
                    alignItems="center">
                    <Text className="color-grey-500">{t('reservation.secondsLeftX', { count: secondsLeft })}</Text>
                    <Button
                        label={t('reservation.continueSession')}
                        onClick={handleContinue}
                        width="auto"
                        className="ml-2"
                        disabled={isRefreshing}
                        loading={isRefreshing} />
                </Flex>
            }>
            <Text>{t('reservation.editSessionExpireInX', { minutes: duration.asMinutes() })}</Text>
        </Modal>
    )
}

export default ContinueSessionModal
